import React from 'react';
import { Check, Clock, Loader2, Mail, Send } from 'lucide-react';

const MessagesTab = ({ ctx }) => {
  const {  API_URL, getToken , messages, msgForm, msgSent, sendingMsg, setMessages, setMsgForm, setMsgSent, setSendingMsg } = ctx;

  return (
<div data-testid="messages-tab">
  <h1 className="text-xl font-bold mb-1" style={{ color: '#1A1209' }}>Contact HiBFF</h1>
  <p className="text-xs mb-5" style={{ color: '#999' }}>
    Send questions, suggestions, or technical queries directly to the HiBFF team. We&apos;ll respond within 24 hours.
  </p>

  {/* Compose form */}
  <div className="bg-white rounded-xl p-5 mb-5" style={{ border: '1px solid #E8E8E8' }} data-testid="message-form">
    <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>New Message</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
      <div>
        <label className="text-[10px] font-medium uppercase tracking-wider block mb-1" style={{ color: '#999' }}>Subject</label>
        <input value={msgForm.subject} onChange={e => setMsgForm(p => ({ ...p, subject: e.target.value }))}
          placeholder="What's this about?" className="w-full px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E8E8E8' }} data-testid="msg-subject" />
      </div>
      <div>
        <label className="text-[10px] font-medium uppercase tracking-wider block mb-1" style={{ color: '#999' }}>Category</label>
        <select value={msgForm.category} onChange={e => setMsgForm(p => ({ ...p, category: e.target.value }))}
          className="w-full px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E8E8E8' }} data-testid="msg-category">
          <option value="general">General Enquiry</option>
          <option value="technical">Technical Support</option>
          <option value="billing">Billing / Subscription</option>
          <option value="suggestion">Feature Suggestion</option>
        </select>
      </div>
    </div>
    <div className="mb-3">
      <label className="text-[10px] font-medium uppercase tracking-wider block mb-1" style={{ color: '#999' }}>Message</label>
      <textarea value={msgForm.message} onChange={e => setMsgForm(p => ({ ...p, message: e.target.value }))}
        placeholder="Describe your question or issue in detail..." rows={4}
        className="w-full px-3 py-2 border rounded-lg text-sm resize-none" style={{ borderColor: '#E8E8E8' }} data-testid="msg-body" />
    </div>
    <button
      onClick={async () => {
        if (!msgForm.subject || !msgForm.message) return;
        setSendingMsg(true); setMsgSent(false);
        try {
          await fetch(`${API_URL}/api/corporate/messages/send`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
            body: JSON.stringify(msgForm),
          });
          setMsgSent(true);
          setMsgForm({ subject: '', message: '', category: 'general' });
          const r = await fetch(`${API_URL}/api/corporate/messages/my`, { headers: { Authorization: `Bearer ${getToken()}` } });
          if (r.ok) { const d = await r.json(); setMessages(d.messages || []); }
        } catch {}
        setSendingMsg(false);
      }}
      disabled={!msgForm.subject || !msgForm.message || sendingMsg}
      className="w-full py-2.5 rounded-lg text-sm font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
      style={{ backgroundColor: '#F5831F' }}
      data-testid="send-msg-btn"
    >
      {sendingMsg ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
      {sendingMsg ? 'Sending...' : 'Send Message'}
    </button>
    {msgSent && (
      <div className="mt-3 p-3 rounded-lg text-center" style={{ backgroundColor: '#ECFDF5' }}>
        <p className="text-xs font-semibold" style={{ color: '#065F46' }}>Message sent! The HiBFF team will respond within 24 hours.</p>
      </div>
    )}
  </div>

  {/* Message history */}
  {messages.length > 0 && (
    <div>
      <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#1A1209' }}>
        <Clock size={14} style={{ color: '#999' }} /> Message History
      </h3>
      <div className="space-y-2">
        {messages.map(m => {
          const catColors = { general: '#2563EB', technical: '#8B5CF6', billing: '#F59E0B', suggestion: '#10B981' };
          const catColor = catColors[m.category] || '#999';
          return (
            <div key={m.message_id} className="bg-white rounded-xl p-4" style={{ border: '1px solid #E8E8E8' }} data-testid={`msg-${m.message_id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ backgroundColor: `${catColor}15`, color: catColor }}>
                    {m.category?.toUpperCase()}
                  </span>
                  <h4 className="text-sm font-bold" style={{ color: '#1A1209' }}>{m.subject}</h4>
                </div>
                <span className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: m.status === 'replied' ? '#059669' : '#999' }}>
                  {m.status === 'replied' ? <Check size={10} /> : <Clock size={10} />}
                  {m.status === 'replied' ? 'Replied' : m.status === 'read' ? 'Read' : 'Sent'}
                </span>
              </div>
              <p className="text-xs mb-2" style={{ color: '#666' }}>{m.message}</p>
              <p className="text-[10px]" style={{ color: '#CCC' }}>
                {m.created_at ? new Date(m.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
              </p>
              {m.replies?.map((r, ri) => (
                <div key={ri} className="mt-3 ml-4 pl-3 border-l-2" style={{ borderColor: '#F5831F' }}>
                  <p className="text-[10px] font-bold" style={{ color: '#F5831F' }}>HiBFF Team</p>
                  <p className="text-xs mt-1" style={{ color: '#333' }}>{r.message}</p>
                  <p className="text-[10px] mt-1" style={{ color: '#CCC' }}>
                    {r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                  </p>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  )}
  {messages.length === 0 && !msgSent && (
    <div className="text-center py-8">
      <Mail size={32} style={{ color: '#DDD' }} className="mx-auto mb-3" />
      <p className="text-sm" style={{ color: '#999' }}>No messages yet. Use the form above to reach the HiBFF team.</p>
    </div>
  )}
</div>
  );
};

export default MessagesTab;
